import React from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { PageHeader } from "@/components/shared";

function LegalShell({ crumb, title, children }) {
  useSeo({ title, description: `${title} for Anvay — Arjunna Soni's Family Constellation practice in Ludhiana.`, path: window.location.pathname });
  return (
    <div>
      <PageHeader crumb={crumb} title={title} />
      <section className="container-page pb-16 md:pb-24 max-w-3xl prose-editorial text-earth/85">
        {children}
      </section>
    </div>
  );
}

export function Privacy() {
  return (
    <LegalShell crumb="Privacy" title="Privacy Policy">
      <p>This policy explains how Arjunna Soni's practice collects, uses, and protects your personal data, in line with India's Digital Personal Data Protection Act, 2023 (DPDP Act).</p>
      <h2>What we collect & why</h2>
      <p>We collect the details you provide when you enquire, book a session, register for a workshop, or subscribe to the newsletter — such as your name, email, phone, and any information you choose to share about what you'd like to work with. This is used solely to respond to you and deliver the service you requested.</p>
      <h2>Sensitive information</h2>
      <p>Any free-text you share about your concerns is treated as sensitive personal data. It is stored securely and seen only by Arjunna.</p>
      <h2>Retention</h2>
      <p>Enquiries are retained for up to 24 months of inactivity. Booking records are retained for 7 years for tax purposes. Session intake notes are purged 12 months after a session unless you remain an active client.</p>
      <h2>Analytics & cookies</h2>
      <p>We use privacy-respecting product analytics to understand how the site is used and to improve it. Session recording is paused on the booking and contact pages to protect the sensitive information you share there. You can block cookies via your browser settings.</p>
      <h2>Your rights</h2>
      <p>You may request access to, correction of, or erasure of your personal data at any time. To do so, contact us at hello@arjunnasoni.in.</p>
      <h2>Grievance officer</h2>
      <p>For any privacy concern, please write to hello@arjunnasoni.in and we will respond promptly.</p>
    </LegalShell>
  );
}

export function Terms() {
  return (
    <LegalShell crumb="Terms" title="Terms & Cancellation Policy">
      <p>By booking a session or registering for a workshop, you agree to the following terms.</p>
      <h2>Individual sessions — cancellation & refunds</h2>
      <ul>
        <li>Full refund if cancelled <strong>72+ hours</strong> before the session.</li>
        <li>50% refund if cancelled between <strong>24–72 hours</strong> before.</li>
        <li>No refund for cancellations under <strong>24 hours</strong>.</li>
        <li>One free reschedule is permitted with at least <strong>48 hours</strong> notice.</li>
      </ul>
      <h2>Workshops — cancellation & refunds</h2>
      <ul>
        <li>Full refund if cancelled <strong>14+ days</strong> before the workshop.</li>
        <li>50% refund if cancelled between <strong>7–14 days</strong> before.</li>
        <li>No refund for cancellations under <strong>7 days</strong>.</li>
      </ul>
      <h2>Conduct & scope</h2>
      <p>Constellation work is a complementary modality and is not a substitute for medical or psychiatric care. Please see our Disclaimer for the full scope of practice.</p>
      <h2>Payment</h2>
      <p>Sessions and workshops are confirmed on receipt of your request. Payment terms are communicated at confirmation.</p>
      <p><Link to="/contact">Contact us</Link> with any questions about these terms.</p>
    </LegalShell>
  );
}

export function Disclaimer() {
  return (
    <LegalShell crumb="Disclaimer" title="Scope of Practice & Disclaimer">
      <p>Please read this carefully before booking.</p>
      <h2>Scope of practice</h2>
      <p>Family & Systemic Constellation work is an <strong>experiential, complementary modality</strong>. It is <strong>not</strong> a substitute for medical or psychiatric treatment or medication, and it is <strong>not appropriate as sole care during an acute mental health crisis</strong>.</p>
      <h2>No guaranteed outcomes</h2>
      <p>We make no claims to cure, diagnose, or treat any medical or psychological condition. Individual experiences vary, and no specific outcome is promised.</p>
      <h2>If you are in crisis</h2>
      <p>If you are experiencing a mental health emergency, please contact a qualified medical professional or a mental health helpline immediately. This work is not a replacement for professional clinical care.</p>
      <h2>Your responsibility</h2>
      <p>You are encouraged to continue any medical or psychological treatment you are receiving. Constellation work is intended to complement, never replace, that care.</p>
    </LegalShell>
  );
}

export function NotFound() {
  useSeo({ title: "Page not found", path: window.location.pathname, noindex: true });
  return (
    <div className="container-page py-24 md:py-32 text-center">
      <div className="text-7xl md:text-8xl text-gold/50 font-display mb-4" style={{ fontWeight: 600 }}>404</div>
      <h1 className="text-3xl md:text-4xl text-earth mb-3" style={{ fontWeight: 600 }}>This page has wandered off.</h1>
      <p className="text-earth/70 mb-8 max-w-md mx-auto">The page you're looking for doesn't exist — but the work is still here whenever you're ready.</p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/" className="btn-gold">Back home</Link>
        <Link to="/book" className="btn-outline">Book a Session</Link>
      </div>
    </div>
  );
}
